import React, { useState } from 'react';
import { ProductData } from '../../types';

interface MultiItemResolverProps {
  imageSrc: string;
  interpretations: { id: string; productData: ProductData }[];
  onSelect: (selected: { id: string; productData: ProductData } | 'collection') => void;
  onCancel: () => void;
}

const MultiItemResolver: React.FC<MultiItemResolverProps> = ({ imageSrc, interpretations, onSelect, onCancel }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | 'collection' | null>(null);

  const handleConfirm = () => {
    if (selectedIndex === 'collection') {
      onSelect('collection');
    } else if (typeof selectedIndex === 'number') {
      onSelect(interpretations[selectedIndex]);
    }
  };

  return (
    <div className="h-dvh flex flex-col overflow-hidden bg-white">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-lg mx-auto p-4 sm:p-6">
          {/* Image preview */}
          <div className="rounded-xl overflow-hidden bg-gray-100 mb-5">
            <img src={imageSrc} alt="Uploaded product" className="w-full h-48 sm:h-56 object-contain bg-gray-50" />
          </div>

          <h2 className="text-lg font-bold text-gray-900 mb-1">What's in this image?</h2>
          <p className="text-sm text-gray-500 mb-5">
            We detected multiple items. Pick the one you're listing, or confirm it's a collection.
          </p>

          {/* Interpretation cards */}
          <div className="space-y-3 mb-6">
            {interpretations.map((item, idx) => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedIndex(idx)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedIndex(idx); } }}
                className={`block p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedIndex === idx
                    ? 'border-blue-600 bg-blue-50/50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="relative flex items-center justify-center shrink-0 mt-0.5">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedIndex === idx ? 'border-blue-600' : 'border-gray-300'
                    }`}>
                      {selectedIndex === idx && <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-900">{item.productData.title}</div>
                    <div className="text-sm text-blue-600 font-medium mt-0.5">
                      ₦{item.productData.price?.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {item.productData.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Collection option */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setSelectedIndex('collection')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedIndex('collection'); } }}
            className={`block p-4 rounded-xl border-2 border-dashed cursor-pointer transition-all mb-6 ${
              selectedIndex === 'collection'
                ? 'border-emerald-500 bg-emerald-50/50 shadow-sm'
                : 'border-gray-300 hover:border-emerald-400 bg-white'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="relative flex items-center justify-center shrink-0 mt-0.5">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedIndex === 'collection' ? 'border-emerald-500' : 'border-gray-300'
                }`}>
                  {selectedIndex === 'collection' && <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">It's a collection — selling them together</div>
                <div className="text-xs text-gray-500 mt-0.5">Combine into a single bundle listing</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="max-w-lg mx-auto flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={selectedIndex === null}
            className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiItemResolver;
